import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/query-keys';
import { useRouter } from '@/lib/navigation';
import { apiFetch } from '@/lib/api/client';
import type { BusinessTripStatus } from '@/hooks/trips/types';

interface SendTripInput {
  tripId: string;
  status: BusinessTripStatus;
}

async function sendTrip({ tripId, status }: SendTripInput): Promise<void> {
  const isResubmission = status === 'returned_back';
  const endpoint = isResubmission ? 'resubmit' : 'submit';

  const response = await apiFetch(`/api/v1/business-trip-requests/${tripId}/${endpoint}`, {
    method: 'POST',
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    const message =
      payload && typeof payload === 'object' && 'error' in (payload as Record<string, unknown>)
        ? String(
            (payload as Record<string, { message?: string }>).error?.message ??
              'Failed to send trip for approval'
          )
        : 'Failed to send trip for approval';

    throw new Error(message);
  }
}

export function useSendTrip() {
  const queryClient = useQueryClient();
  const router = useRouter();

  return useMutation({
    mutationFn: sendTrip,
    onSuccess: (_result, variables) => {
      const tripId = variables?.tripId;

      // Invalidate trips list to reflect the new status
      queryClient.invalidateQueries({ queryKey: queryKeys.trips });

      // Invalidate the specific trip to refresh its status
      if (tripId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.trip(tripId) });
      }

      // Redirect to the homepage after successful send
      router.push('/');
    },
    onError: (error) => {
      console.error('Error sending trip:', error);
    },
  });
}
